import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Anonymous or expired session — leave user null. Log only unexpected errors.
      if (err?.response?.status && err.response.status !== 401) {
        console.debug("auth/me failed:", err.response.status);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Convenience helpers for Fase 1 plan gating (kept close to the auth state
  // so screens don't need extra fetches). `null` while loading.
  const plan = user?.plan || null;
  const effectivePlan = user?.effective_plan || null;
  const isPro = effectivePlan === "pro";
  const isFree = effectivePlan === "free";
  const hasFeature = useCallback(
    (feature) => Boolean(user?.features?.[feature]) || Boolean(user?.grandfathered && ["multi_business", "extra_cards"].includes(feature)),
    [user]
  );

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Even if the server call fails we still clear the local session
      console.debug("logout API call failed, clearing local state:", err?.message);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refresh: checkAuth, plan, effectivePlan, isPro, isFree, hasFeature }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
